import React from "react";
import { Link } from "react-router-dom";
import { createPageStyles } from "./MarketingPageStyles";
import { detectPageLocale } from "./pageLocale";

const styles = createPageStyles("dark", "luna");

type Locale = "ko" | "en" | "ja";
type Row = [string, string, string, string];

const LOCALE_STORAGE_KEY = "addup-privacy-locale";
const locales: Locale[] = ["ko", "en", "ja"];
const localeLabels: Record<Locale, string> = { ko: "KO", en: "EN", ja: "JA" };

const tableRows: Record<Locale, Row[]> = {
  ko: [
    ["로컬 할 일 데이터", "할 일·체크리스트·메모, 일정 설정(날짜/시간/반복/알림), 앱 설정값", "할 일 관리, 위젯·Apple Watch 표시", "사용자 기기 내 (앱 로컬 저장소)"],
    ["iCloud 동기화", "할 일 데이터", "같은 사용자의 기기 간 동기화", "사용자 본인의 iCloud (Apple, 개발자 접근 불가)"],
    ["Firebase Analytics / Crashlytics", "앱 버전, 충돌 로그, 익명 기기·사용 정보", "앱 안정성 개선, 오류 분석, 기능 개선", "Firebase 서버 (Google LLC)"],
    ["푸시 알림 (Firebase Cloud Messaging)", "푸시 토큰, 기기 정보", "알림 발송", "Firebase 서버 (Google LLC)"],
    ["구독 결제", "앱은 결제정보를 수집하지 않음 (구매 영수증은 Apple이 처리)", "구독 상태 확인", "Apple App Store"],
  ],
  en: [
    ["Local task data", "Tasks, checklists, memos, schedule settings (date/time/repeat/reminder), app settings", "Task management, widget and Apple Watch display", "On device (local app storage)"],
    ["iCloud sync", "Task data", "Sync across the same user's devices", "Your own iCloud (Apple; not accessible by the developer)"],
    ["Firebase Analytics / Crashlytics", "App version, crash logs, anonymous device/usage information", "Stability improvement, error analysis, feature improvement", "Firebase servers (Google LLC)"],
    ["Push notifications (Firebase Cloud Messaging)", "Push token, device information", "Delivering notifications", "Firebase servers (Google LLC)"],
    ["Subscription billing", "The app does not collect payment data (receipts are handled by Apple)", "Verifying subscription status", "Apple App Store"],
  ],
  ja: [
    ["ローカル To Do データ", "To Do・チェックリスト・メモ、予定設定（日付/時刻/繰り返し/通知）、アプリ設定", "To Do 管理、ウィジェット・Apple Watch 表示", "ユーザー端末内（ローカル保存）"],
    ["iCloud 同期", "To Do データ", "同一ユーザーの端末間同期", "ユーザー本人の iCloud（Apple、開発者はアクセス不可）"],
    ["Firebase Analytics / Crashlytics", "アプリバージョン、クラッシュログ、匿名の端末・利用情報", "安定性改善、エラー分析、機能改善", "Firebase サーバー（Google LLC）"],
    ["プッシュ通知 (Firebase Cloud Messaging)", "プッシュトークン、端末情報", "通知の配信", "Firebase サーバー（Google LLC）"],
    ["サブスク決済", "アプリは決済情報を収集しません（レシートは Apple が処理）", "サブスク状態の確認", "Apple App Store"],
  ],
};

const content: Record<
  Locale,
  {
    crumb: string;
    title: string;
    accentTitle: string;
    subtitle: string;
    tableHead: Row;
    sections: Array<{ title: string; body?: string; list?: string[] }>;
    websiteStats: { title: string; body: string; list: string[] };
  }
> = {
  ko: {
    crumb: "AddUp Privacy",
    title: "AddUp,",
    accentTitle: "개인정보 처리방침",
    subtitle:
      "최종 업데이트일: 2026년 6월 14일. 본 정책의 운영 주체는 Daydream Factory입니다. AddUp은 회원가입 없이 사용할 수 있으며, 할 일 데이터는 기기와 사용자 본인의 iCloud에 저장됩니다. 별도의 서버 계정에 개인정보를 수집하지 않습니다.",
    tableHead: ["구분", "수집 항목", "사용 목적", "저장 위치"],
    sections: [
      { title: "1. 수집하는 정보" },
      {
        title: "2. 데이터 사용 목적",
        list: [
          "할 일·일정 관리 및 위젯·Apple Watch 표시",
          "알림(미리 알림) 발송",
          "앱 안정성 개선 및 오류 분석",
          "구독(AddUp Pro) 상태 확인",
        ],
      },
      {
        title: "3. 제3자 처리 위탁",
        body: "AddUp은 아래 서비스를 통해 일부 데이터를 처리합니다. 각 서비스의 개인정보 처리방침이 함께 적용됩니다.",
        list: [
          "Firebase (Google LLC)",
          "Apple iCloud / App Store (Apple Inc.)",
        ],
      },
      {
        title: "4. 데이터 보관 및 삭제",
        list: [
          "할 일 데이터는 앱을 삭제하면 기기에서 함께 삭제됩니다.",
          "iCloud에 동기화된 데이터는 기기의 iCloud 설정 또는 앱 내 '전체 초기화'로 삭제할 수 있습니다.",
          "Firebase 분석/충돌 데이터는 Google의 보관 정책에 따릅니다.",
        ],
      },
      {
        title: "5. 이용자 권리 및 문의",
        body:
          "이용자는 언제든 앱 삭제 또는 '전체 초기화'로 데이터를 삭제할 수 있습니다. 개인정보 관련 문의: support@daydreamfactory.net",
      },
    ],
    websiteStats: {
      title: "6. 웹사이트 정보",
      body: "본 페이지(daydreamfactory.net)는 방문 통계 측정을 위해 익명화된 분석 도구를 사용할 수 있습니다.",
      list: ["수집 항목: 페이지 조회, 익명 기기/브라우저 정보", "목적: 사이트 개선"],
    },
  },
  en: {
    crumb: "AddUp Privacy",
    title: "AddUp,",
    accentTitle: "Privacy Policy",
    subtitle:
      "Last updated: June 14, 2026. This policy is operated by Daydream Factory. AddUp can be used without an account, and task data is stored on your device and in your own iCloud. We do not collect personal data into a separate server account.",
    tableHead: ["Category", "Collected items", "Purpose", "Storage location"],
    sections: [
      { title: "1. Information We Collect" },
      {
        title: "2. How We Use Data",
        list: [
          "Task and schedule management, widget and Apple Watch display",
          "Sending reminders/notifications",
          "Improving app stability and analyzing errors",
          "Verifying subscription (AddUp Pro) status",
        ],
      },
      {
        title: "3. Third-Party Processing",
        body: "AddUp processes some data through the services below. Each service's own privacy policy also applies.",
        list: [
          "Firebase (Google LLC)",
          "Apple iCloud / App Store (Apple Inc.)",
        ],
      },
      {
        title: "4. Data Retention and Deletion",
        list: [
          "Task data is deleted from the device when you delete the app.",
          "Data synced to iCloud can be removed via your device's iCloud settings or the in-app 'Reset everything' feature.",
          "Firebase analytics/crash data follows Google's retention policy.",
        ],
      },
      {
        title: "5. Your Rights and Contact",
        body:
          "You can delete your data at any time by deleting the app or using 'Reset everything'. Privacy inquiries: support@daydreamfactory.net",
      },
    ],
    websiteStats: {
      title: "6. Website Information",
      body: "This page (daydreamfactory.net) may use anonymized analytics to measure visit statistics.",
      list: ["Collected: page views, anonymous device/browser information", "Purpose: site improvement"],
    },
  },
  ja: {
    crumb: "AddUp Privacy",
    title: "AddUp,",
    accentTitle: "プライバシーポリシー",
    subtitle:
      "最終更新日: 2026年6月14日。本ポリシーの運営主体は Daydream Factory です。AddUp は会員登録なしで利用でき、To Do データは端末およびユーザー本人の iCloud に保存されます。別途のサーバーアカウントに個人情報を収集しません。",
    tableHead: ["区分", "収集項目", "利用目的", "保存場所"],
    sections: [
      { title: "1. 収集する情報" },
      {
        title: "2. データの利用目的",
        list: [
          "To Do・予定の管理、ウィジェット・Apple Watch 表示",
          "リマインダー/通知の送信",
          "アプリの安定性改善とエラー分析",
          "サブスク (AddUp Pro) 状態の確認",
        ],
      },
      {
        title: "3. 第三者への処理委託",
        body: "AddUp は以下のサービスを通じて一部データを処理します。各サービスのプライバシーポリシーも併せて適用されます。",
        list: [
          "Firebase (Google LLC)",
          "Apple iCloud / App Store (Apple Inc.)",
        ],
      },
      {
        title: "4. データの保管と削除",
        list: [
          "To Do データはアプリを削除すると端末から削除されます。",
          "iCloud に同期されたデータは、端末の iCloud 設定またはアプリ内の「すべて初期化」で削除できます。",
          "Firebase の分析/クラッシュデータは Google の保管ポリシーに従います。",
        ],
      },
      {
        title: "5. ユーザーの権利とお問い合わせ",
        body:
          "ユーザーはいつでもアプリの削除または「すべて初期化」でデータを削除できます。プライバシーに関するお問い合わせ: support@daydreamfactory.net",
      },
    ],
    websiteStats: {
      title: "6. ウェブサイト情報",
      body: "本ページ（daydreamfactory.net）は訪問統計の測定のため匿名化された分析ツールを使用する場合があります。",
      list: ["収集項目: ページ閲覧、匿名の端末/ブラウザ情報", "目的: サイト改善"],
    },
  },
};

const AddUpPrivacyPolicy: React.FC = () => {
  const [locale, setLocale] = React.useState<Locale>("en");

  React.useEffect(() => {
    setLocale(detectPageLocale(LOCALE_STORAGE_KEY, locales, "en"));
  }, []);

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
      document.documentElement.lang = locale;
    }
  }, [locale]);

  const copy = content[locale];

  return (
    <main style={styles.page}>
      <div style={styles.shell}>
        <div style={styles.topRow}>
          <Link to="/" style={styles.homeLink}>
            ← Daydream Factory
          </Link>
          <div style={styles.topRowMeta}>
            <div style={styles.localeSwitch} aria-label="Language switcher">
              {locales.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setLocale(item)}
                  style={{
                    ...styles.localeButton,
                    ...(locale === item ? styles.activeLocaleButton : {}),
                  }}
                >
                  {localeLabels[item]}
                </button>
              ))}
            </div>
            <span style={styles.crumb}>{copy.crumb}</span>
          </div>
        </div>

        <section style={styles.hero}>
          <h1 style={styles.title}>
            {copy.title}
            <br />
            <span style={styles.accentTitle}>{copy.accentTitle}</span>
          </h1>
          <p style={styles.subtitle}>{copy.subtitle}</p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>{copy.sections[0].title}</h2>
          <div style={styles.sectionCard}>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    {copy.tableHead.map((item) => (
                      <th key={item} style={styles.thtd}>
                        {item}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tableRows[locale].map((row) => (
                    <tr key={row[0]}>
                      {row.map((cell) => (
                        <td key={cell} style={styles.thtd}>
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {copy.sections.slice(1).map((section) => (
          <section key={section.title} style={styles.section}>
            <h2 style={styles.h2}>{section.title}</h2>
            <div style={styles.sectionCard}>
              {section.body ? (
                <p style={styles.body}>
                  {section.body.includes("support@daydreamfactory.net") ? (
                    <>
                      {section.body.replace("support@daydreamfactory.net", "")}
                      <a href="mailto:support@daydreamfactory.net" style={styles.link}>
                        support@daydreamfactory.net
                      </a>
                    </>
                  ) : (
                    section.body
                  )}
                </p>
              ) : null}
              {section.list ? (
                <ul style={styles.list}>
                  {section.list.map((item) => (
                    <li key={item}>
                      {item === "Firebase (Google LLC)" ? (
                        <>
                          Firebase (Google LLC) -{" "}
                          <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noreferrer" style={styles.link}>
                            Firebase Privacy &amp; Security
                          </a>
                        </>
                      ) : item.startsWith("Apple iCloud") ? (
                        <>
                          Apple iCloud / App Store (Apple Inc.) -{" "}
                          <a href="https://www.apple.com/legal/privacy/" target="_blank" rel="noreferrer" style={styles.link}>
                            Apple Privacy Policy
                          </a>
                        </>
                      ) : (
                        item
                      )}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </section>
        ))}

        <section style={styles.section}>
          <h2 style={styles.h2}>{copy.websiteStats.title}</h2>
          <div style={styles.sectionCard}>
            <p style={styles.body}>{copy.websiteStats.body}</p>
            <ul style={styles.list}>
              {copy.websiteStats.list.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        </section>
      </div>
    </main>
  );
};

export default AddUpPrivacyPolicy;
